import { DOMAIN, isAdminDomain } from "~/logic"
import { useAuthStore, useEntrepriseStore } from "~/stores"
import { Logger } from "~/helpers/logger"

class RouteMiddleware {
    constructor(to, from, next) {
        this.to = to
        this.from = from
        this.next = next

        this.authStore = useAuthStore()

        Logger.info("AUTH MIDDLEWARE", `Routing to : ${this.to.name}`)
    }

    get isAdminRoute() {
        return this.to.name?.startsWith(DOMAIN.admin.prefix)
    }

    get isManagerRoute() {
        return this.to.name?.startsWith(DOMAIN.manager.prefix)
    }

    get isSpaceRoute() {
        return this.to.name?.startsWith(DOMAIN.space.prefix)
    }

    get isUserAdmin() {
        return this.authStore.isAdmin
    }

    get isUserPersonnel() {
        return this.authStore.isPersonnel
    }

    get isUserClient() {
        return this.authStore.isClient
    }

    get isAdminDomain() {
        return isAdminDomain
    }

    get isAuthenticated() {
        return this.authStore.isTokenSet
    }

    async handle() {
        await this.authStore.ready

        // Autoriser les routes publiques sans authentification
        if (this.to.meta?.public) {
            Logger.info("AUTH MIDDLEWARE", "Public route, no auth required")
            return this.next()
        }

        if (!this.isAuthenticated) {
            Logger.info("AUTH MIDDLEWARE", "Not authenticated")
            return this.redirectToLogin()
        }

        const entrepriseStore = useEntrepriseStore()
        // Ne mettre à jour que si la company_id a réellement changé
        // pour éviter de déclencher inutilement le watcher de pinia-idb
        if (
            entrepriseStore.currentItemId !==
            this.authStore.mainToken.company_id
        ) {
            entrepriseStore.currentItemId = this.authStore.mainToken.company_id
        }

        // Check access
        if (this.isAdminDomain) {
            return this.handleAdminDomain()
        }
        return this.handleCompanyDomain()
    }

    redirectToLogin() {
        // la cible initiale (inclut query + hash)
        const redirect = this.to.fullPath
        const query = { redirect }

        // évite les boucles si on est déjà sur une page d’auth
        const isOnAnyLogin =
            this.to.name === DOMAIN.admin.login ||
            this.to.name === DOMAIN.manager.login ||
            this.to.name === DOMAIN.space.login ||
            this.to.name === "auth.password.request"

        if (isOnAnyLogin) return this.next()

        if (this.isAdminDomain) {
            console.log("Redirecting to admin login")
            return this.#redirectTo(DOMAIN.admin.login, query)
        }

        // sur les sous-domaines company, choisis le login le plus logique
        if (this.isManagerRoute)
            return this.#redirectTo(DOMAIN.manager.login, query)
        return this.#redirectTo(DOMAIN.space.login, query)
    }

    // @authenticated
    handleAdminDomain() {
        if (this.isUserAdmin && !this.isAdminRoute)
            return this.#redirectTo(DOMAIN.admin.home)

        if (this.isUserClient && !this.isSpaceRoute)
            return this.#redirectTo(DOMAIN.space.home)

        if (this.isUserPersonnel && !this.isManagerRoute) {
            return this.#redirectTo(DOMAIN.manager.home)
        }

        this.next()
    }

    // @authenticated
    handleCompanyDomain() {
        if (this.isUserAdmin)
            throw new Error(
                "Vous ne pouvez accedez a un sous domaine company en tant qu'utilisateur admin.",
            )
        if (this.isUserClient && !this.isSpaceRoute)
            return this.#redirectTo(DOMAIN.space.home)

        if (this.isUserPersonnel && !this.isManagerRoute)
            return this.#redirectTo(DOMAIN.manager.home)

        this.next()
    }

    #redirectTo(name, query) {
        Logger.info("AUTH MIDDLEWARE", `Redirecting to : ${name}`, query)
        if (this.to.name !== name || query) {
            this.next({ name, ...(query ? { query } : {}) })
        } else {
            this.next()
        }
    }
}

export const AutMiddleware = async (to, from, next) => {
    const routeMiddleware = new RouteMiddleware(to, from, next)
    await routeMiddleware.handle()
}
